import { Box, Chip, Stack, TextField, Typography } from "@mui/material";
import type { LocalizedString } from "@shared/types/foyer";
import { localeMeta, setLocalized } from "@shared/i18n";
import { useTranslation } from "react-i18next";

interface Props {
  label?: string;
  value: LocalizedString | undefined;
  supportedLocales: string[];
  defaultLocale: string;
  onChange: (next: LocalizedString) => void;
  multiline?: boolean;
  rows?: number;
  placeholder?: string;
  helperText?: string;
  testId?: string;
}

/**
 * One input per supported locale. Default locale on top labelled
 * "DEFAULT"; other locales below as smaller secondary inputs. Empty
 * non-default locales are pruned on save by `setLocalized`, so content
 * stays as a plain string when only the default has text.
 */
export default function LocalizedTextField({
  label,
  value,
  supportedLocales,
  defaultLocale,
  onChange,
  multiline,
  rows,
  placeholder,
  helperText,
  testId,
}: Props) {
  const { t } = useTranslation("templates");
  const list = supportedLocales.length > 0 ? supportedLocales : [defaultLocale];
  // Render default first, then the rest in catalogue order.
  const ordered = [defaultLocale, ...list.filter((l) => l !== defaultLocale)];

  function read(locale: string): string {
    if (!value) return "";
    if (typeof value === "string") {
      // Plain string shows only on the default-locale input so the
      // user can see the existing copy and decide whether to localise.
      return locale === defaultLocale ? value : "";
    }
    return value[locale] ?? "";
  }

  function write(locale: string, next: string) {
    onChange(setLocalized(value, locale, defaultLocale, next));
  }

  return (
    <Stack spacing={1}>
      {label && (
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      )}
      {ordered.map((locale) => {
        const isDefault = locale === defaultLocale;
        const m = localeMeta(locale);
        return (
          <Box key={locale} sx={{ position: "relative" }}>
            <TextField
              fullWidth
              size="small"
              value={read(locale)}
              onChange={(e) => write(locale, e.target.value)}
              multiline={multiline}
              rows={rows}
              placeholder={
                isDefault
                  ? placeholder
                  : t("localizedTextField.translationPlaceholder", {
                      language: m.native,
                    })
              }
              InputProps={{
                startAdornment: (
                  <Chip
                    label={
                      isDefault
                        ? t("localizedTextField.defaultChip", {
                            code: m.code.toUpperCase(),
                          })
                        : m.code.toUpperCase()
                    }
                    size="small"
                    color={isDefault ? "primary" : "default"}
                    variant={isDefault ? "filled" : "outlined"}
                    sx={{ mr: 1, height: 22 }}
                  />
                ),
              }}
              inputProps={
                testId ? { "data-testid": `${testId}-${locale}` } : undefined
              }
            />
          </Box>
        );
      })}
      {helperText && (
        <Typography variant="caption" color="text.secondary">
          {helperText}
        </Typography>
      )}
    </Stack>
  );
}
